import { useMemo } from 'react';
import i18n, { useCurrentLanguage } from './index';

/**
 * CORE-1306 — the date vocabulary of the CURRENT UI language.
 *
 * Native screens used to compose dates by hand from Russian arrays («21 июн.»,
 * «Событие на 17 июня», «ПН ВТ СР»), so switching the language left every
 * date Russian. Hermes ships a trimmed Intl, and the screens deliberately
 * format without it — this module keeps that approach and only swaps the
 * words: they come from `common:dates.*`, one table per language.
 */
export interface DateNames {
  /** «Январь» — capitalised, for month headers. */
  monthsFull: string[];
  /** «янв» — for compact date chips. */
  monthsShort: string[];
  /** «января» — the form that follows a day number («3 июня»). */
  monthsGenitive: string[];
  /** «Вс … Сб», Sunday-first, matching `Date#getDay()`. */
  weekdaysShort: string[];
  /** «{{day}} {{month}}» / «{{month}} {{day}}» / «{{day}} de {{month}}». */
  dayMonth: string;
  dayMonthShort: string;
  today: string;
  tomorrow: string;
  yesterday: string;
}

// The product default, and the safety net if a table is ever missing.
const RU: DateNames = {
  monthsFull: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  monthsShort: ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'],
  monthsGenitive: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
  weekdaysShort: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
  dayMonth: '{{day}} {{month}}',
  dayMonthShort: '{{day}} {{month}}',
  today: 'Сегодня',
  tomorrow: 'Завтра',
  yesterday: 'Вчера',
};

function table(key: string, fallback: string[]): string[] {
  const value = i18n.t(key, { returnObjects: true }) as unknown;
  return Array.isArray(value) && value.length === fallback.length ? (value as string[]) : fallback;
}

function text(key: string, fallback: string): string {
  const value = i18n.t(key) as unknown;
  return typeof value === 'string' && value !== key ? value : fallback;
}

/**
 * Plain read for the current language. Not reactive on its own — components
 * go through `useDateNames()` so a language switch re-renders their dates.
 */
export function getDateNames(): DateNames {
  return {
    monthsFull: table('dates.monthsFull', RU.monthsFull),
    monthsShort: table('dates.monthsShort', RU.monthsShort),
    monthsGenitive: table('dates.monthsGenitive', RU.monthsGenitive),
    weekdaysShort: table('dates.weekdaysShort', RU.weekdaysShort),
    dayMonth: text('dates.dayMonth', RU.dayMonth),
    dayMonthShort: text('dates.dayMonthShort', RU.dayMonthShort),
    today: text('dates.today', RU.today),
    tomorrow: text('dates.tomorrow', RU.tomorrow),
    yesterday: text('dates.yesterday', RU.yesterday),
  };
}

export function useDateNames(): DateNames {
  const lang = useCurrentLanguage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => getDateNames(), [lang]);
}

const fill = (template: string, day: number, month: string): string =>
  template.replace('{{day}}', String(day)).replace('{{month}}', month);

/** «3 июня» · «June 3» · «3 de junio». */
export const formatDayMonth = (d: Date, names: DateNames): string =>
  fill(names.dayMonth, d.getDate(), names.monthsGenitive[d.getMonth()]);

/**
 * The same, but the day of the instant in UTC — for a date the server names
 * the same for everyone (CORE-1479: the reward deadline, which Agapella says
 * by UTC too).
 */
export const formatDayMonthUtc = (d: Date, names: DateNames): string =>
  fill(names.dayMonth, d.getUTCDate(), names.monthsGenitive[d.getUTCMonth()]);

/** «3 июн» · «Jun 3» · «3 jun». */
export const formatDayMonthShort = (d: Date, names: DateNames): string =>
  fill(names.dayMonthShort, d.getDate(), names.monthsShort[d.getMonth()]);
